/**
 * Dial event.log reducer. Engine lines are appended as sent.
 *
 * Do not rewrite `log.operator` / `session.ended` from a stale snapshot.
 * A SCREENING line already on screen must not eat a later Human or VM line
 * (push-dedupe only skips an identical last line).
 */

import { amdLabelFromOperatorLine, historyAmd } from "./historyOutcome";
import type { LogLine } from "./types";
import { uid } from "./format";

const MAX_LOG = 250;

export interface OperatorFrame {
  type: string;
  line?: string;
  ts?: string;
  amd?: string | null;
}

export interface OperatorFrameUpdate {
  /** Engine line, unchanged. Empty when this frame is not an operator log. */
  line: string;
  amdLabel: string;
  append: boolean;
}

/**
 * Decide the next AMD label and whether to append `frame.line` as sent.
 * `session.state` does not synthesize a step — `log.operator` is the log.
 */
export function operatorFrameUpdate(
  amdLabel: string,
  frame: OperatorFrame,
): OperatorFrameUpdate {
  if (frame.type === "session.state") {
    return { line: "", amdLabel, append: false };
  }
  if (frame.type !== "log.operator" && frame.type !== "session.ended") {
    return { line: "", amdLabel, append: false };
  }
  const line = frame.line ?? "";
  const fromLine = amdLabelFromOperatorLine(line);
  const fromAmd = historyAmd(frame.amd);
  return {
    line,
    amdLabel: fromLine || fromAmd || amdLabel,
    append: Boolean(line),
  };
}

/** Append one engine line. Consecutive duplicates are skipped. Never rewrites. */
export function appendOperatorLine(lines: LogLine[], line: string, ts?: string): LogLine[] {
  if (!line) return lines;
  if (lines.length && lines[lines.length - 1].line === line) return lines;
  const next: LogLine = { id: uid("log"), ts: ts ?? new Date().toISOString(), line };
  const merged = [...lines, next];
  return merged.length > MAX_LOG ? merged.slice(merged.length - MAX_LOG) : merged;
}
